/**
 * Single source of truth for chat bubble sizes (XS–2XL).
 * Sizes are defined in rem for use inside the iframe; px values for the host
 * iframe dimensions are derived here (base 16px = 1rem + spacing for shadow/hover).
 */

export const BUBBLE_SIZE_KEYS = [
	'xs',
	'sm',
	'md',
	'lg',
	'xl',
	'2xl',
] as const;
export type BubbleSizeKey = ( typeof BUBBLE_SIZE_KEYS )[ number ];

export const DEFAULT_BUBBLE_SIZE: BubbleSizeKey = 'lg';

/** Rem-based sizes for the toggle button (used inside iframe). */
export const BUBBLE_SIZES: Record<
	BubbleSizeKey,
	{ iconRem: number; borderPx: number; marginRem: number }
> = {
	xs: { iconRem: 2.25, borderPx: 3, marginRem: 0.75 },
	sm: { iconRem: 2.75, borderPx: 3, marginRem: 1 },
	md: { iconRem: 3.25, borderPx: 3, marginRem: 1.25 },
	lg: { iconRem: 4, borderPx: 4, marginRem: 1.5 },
	xl: { iconRem: 4.75, borderPx: 4, marginRem: 1.75 },
	'2xl': { iconRem: 5.5, borderPx: 5, marginRem: 2 },
};

const BASE_FONT_SIZE_PX = 16;
/** Extra px around the button for box-shadow and hover scale. */
const IFRAME_PADDING_PX = 12;

/** The sticky bar is this fraction of the bubble diameter tall. */
const STICKY_BAR_HEIGHT_RATIO = 0.8;
/** Never let the sticky bar get too small to hit comfortably. */
const STICKY_BAR_MIN_PX = 40;

/**
 * Compute iframe dimensions in px from the rem-based config.
 * Used by the host page so the iframe size is always in pixels (host rem may differ).
 * @param key
 * @param baseFontSizePx
 */
export function getIframeSizePx(
	key: string,
	baseFontSizePx: number = BASE_FONT_SIZE_PX
): { width: number; height: number } {
	const size = BUBBLE_SIZES[ key as BubbleSizeKey ];
	if ( ! size ) {
		const fallback = BUBBLE_SIZES[ DEFAULT_BUBBLE_SIZE ];
		return getIframeSizePxFromConfig( fallback, baseFontSizePx );
	}
	return getIframeSizePxFromConfig( size, baseFontSizePx );
}

function getIframeSizePxFromConfig(
	config: { iconRem: number; borderPx: number; marginRem: number },
	baseFontSizePx: number
): { width: number; height: number } {
	const iconPx = config.iconRem * baseFontSizePx;
	const marginPx = config.marginRem * baseFontSizePx;
	const buttonPx = iconPx + 2 * config.borderPx;
	const total = Math.ceil( buttonPx + 2 * marginPx + IFRAME_PADDING_PX );
	return { width: total, height: total };
}

/**
 * Toggle / sticky-bar diameter in px (icon + both borders) at the 16px rem base.
 * Used by the launcher so the search bar and collapsed bubble share one size.
 * @param key
 */
export function getBubbleButtonPx( key: string ): number {
	const size =
		BUBBLE_SIZES[ key as BubbleSizeKey ] ??
		BUBBLE_SIZES[ DEFAULT_BUBBLE_SIZE ];
	return size.iconRem * BASE_FONT_SIZE_PX + 2 * size.borderPx;
}

/**
 * Height of the sticky search bar in px.
 *
 * The bar scales with the configured bubble size but stays slimmer than the
 * bubble diameter — a full-diameter input bar reads as a heavy block across the
 * viewport, while the round bubble needs its full size to stay a circle.
 * @param key
 */
export function getStickyBarPx( key: string ): number {
	return Math.max(
		STICKY_BAR_MIN_PX,
		Math.round( getBubbleButtonPx( key ) * STICKY_BAR_HEIGHT_RATIO )
	);
}

/**
 * For StickyChat: CSS values (rem/px strings) from the config.
 * @param key
 */
export function getBubbleSizeCss( key: string ): {
	icon: string;
	border: string;
	margin: string;
} {
	const size =
		BUBBLE_SIZES[ key as BubbleSizeKey ] ??
		BUBBLE_SIZES[ DEFAULT_BUBBLE_SIZE ];
	return {
		icon: `${ size.iconRem }rem`,
		border: `${ size.borderPx }px`,
		margin: `${ size.marginRem }rem`,
	};
}

/**
 * Icon size in px at base font size (for admin preview).
 * @param key
 */
export function getIconSizePx( key: string ): number {
	const size =
		BUBBLE_SIZES[ key as BubbleSizeKey ] ??
		BUBBLE_SIZES[ DEFAULT_BUBBLE_SIZE ];
	return Math.round( size.iconRem * BASE_FONT_SIZE_PX );
}

/** Space between the open panel and the bubble below it. */
export const BUBBLE_PANEL_GAP_PX = 16;

/**
 * Bottom offset of the compact panel when the bubble is rendered: corner
 * margin + bubble diameter + the gap above it, in px at the 16px rem base.
 * Mirrors the `--dhsv-panel-stack` calc in `iframe-content.scss`, so the table
 * in the spec (70, 82, 94, 112, 128, 146) is testable.
 * @param key
 */
export function getBubbleStackPx( key: string ): number {
	const size =
		BUBBLE_SIZES[ key as BubbleSizeKey ] ??
		BUBBLE_SIZES[ DEFAULT_BUBBLE_SIZE ];
	return (
		size.marginRem * BASE_FONT_SIZE_PX +
		getBubbleButtonPx( key ) +
		BUBBLE_PANEL_GAP_PX
	);
}

/** Theme-hook custom properties describing the bubble to the panel CSS. */
export const BUBBLE_TOKEN_NAMES = [
	'--dhsv-bubble-diameter',
	'--dhsv-bubble-margin',
	'--dhsv-bubble-gap',
] as const;

/**
 * Write the bubble geometry as inline custom properties on `element` (the one
 * that carries `.tailwind`, so no declaration there can shadow them). Returns
 * the cleanup that removes them again, which lets the stylesheet defaults or a
 * style-config override apply once no bubble is rendered.
 * @param element
 * @param key
 */
export function applyBubbleTokens(
	element: HTMLElement,
	key: string
): () => void {
	element.style.setProperty(
		'--dhsv-bubble-diameter',
		`${ getBubbleButtonPx( key ) }px`
	);
	element.style.setProperty(
		'--dhsv-bubble-margin',
		getBubbleSizeCss( key ).margin
	);
	element.style.setProperty(
		'--dhsv-bubble-gap',
		`${ BUBBLE_PANEL_GAP_PX / BASE_FONT_SIZE_PX }rem`
	);
	return () => {
		BUBBLE_TOKEN_NAMES.forEach( ( name ) =>
			element.style.removeProperty( name )
		);
	};
}
